import {useMemo} from "react";
import {chunk} from "lodash";
import {IQueryParamsContext} from "@hooks/query/types/queryParams";
import {getCurrentSearchKey} from "@hooks/pagination/helpers/getCurrentSearchKey";
import {filterArray} from "@hooks/filters/helpers/filter";

interface IProps<T> {
    data: T[],
    params: IQueryParamsContext<T>,
    searchKeysArr?: (keyof T)[],
}


export function usePaginationCustomWithFilters<T>({
                                                       data,
                                                       params,
                                                       searchKeysArr,
                                                   }: IProps<T>): {
    paginationData: T[],
    dataWithFilters: T[],
    totalPage: number,
} {

    const {
        page,
        perPage,
        order,
        direction,
        searchValue,
        searchKey,
    } = params

    const currentData: {
        chunks: T[][],
        dataWithFilters: T[],
        totalPage: number,
    } = useMemo(() => {

        if (!searchValue && !order) {
            const chunks = chunk(data, perPage)
            return {
                chunks,
                totalPage: chunks.length,
                dataWithFilters: data,
            }
        }

        const currentSearchKey = getCurrentSearchKey({
            searchKeysArr,
            searchKey
        })
        const filteredData = filterArray({
            arr: data,
            order,
            direction,
            searchValue,
            searchKey: currentSearchKey,
        });

        const chunks = chunk(filteredData, perPage)

        return {
            chunks,
            totalPage: chunks.length,
            dataWithFilters: filteredData,
        }
    }, [
        data,
        perPage,
        searchValue,
        searchKey,
        searchKeysArr,
        order,
        direction,
    ])

    const paginationData = useMemo(() => {
        if (perPage?.toString() === '0' || page < 1) {
            return data
        }
        const currentPage = currentData.chunks.length <= page - 1 ? currentData.chunks.length - 1 : page - 1
        return currentData.chunks[currentPage]

    }, [currentData, data, page, perPage])

    return {
        paginationData,
        dataWithFilters: currentData?.dataWithFilters,
        totalPage: currentData.totalPage || 1
    }

}